<template>
  <div class="QuestionUsage">
    <template v-if="types.length">
      <FormField :statement="t(`${i18nRoot}_${id}-select-type-label`)">
        <RadioButtonGroupComponent
          :id="`${id}-type`"
          :options="types"
          :value="defaultType"
          v-model="question!.type"
          flexDirection="row"
          flexWrap="wrap"
          :rules="[required]"
          :scroll="false"
        />
      </FormField>

      <BaseInput
        :label="t(`${i18nRoot}_${id}-amount-label`)"
        :id="`${id}-amount`"
        :maxlength="3"
        :model-value="question!.amount"
        @update:model-value="setAmount"
        :placeholder="t(`${i18nRoot}_${id}-amount-placeholder`)"
        :rules="[required]"
        @keypress="isNumberKey"
      />
    </template>

    <template v-else>
      <BaseInput
        :label="t(`${i18nRoot}_${id}-amount-label`)"
        :id="`${id}-amount`"
        :maxlength="3"
        :model-value="question!.amount"
        @update:model-value="setAmount"
        :placeholder="t(`${i18nRoot}_${id}-amount-placeholder`)"
        :rules="[required]"
        @keypress="isNumberKey"
      />

      <BaseInput
        :id="`${id}-type`"
        :label="t(`${i18nRoot}_${id}-type-label`)"
        v-model="question!.type"
        :maxlength="1024"
        :placeholder="t(`${i18nRoot}_${id}-type-placeholder`)"
        :rules="[required]"
      />

      <BaseInput
        :label="t(`${i18nRoot}_${id}-frequency-label`)"
        :id="`${id}-frequency`"
        v-model="question!.frequency"
        :maxlength="1024"
        :placeholder="t(`${i18nRoot}_${id}-frequency-placeholder`)"
        :rules="[required]"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import QuestionUsage from '@/modules/common/common/models/APP/questionnaire/QuestionUsage'
  import FormField from '@/modules/core/components/utils/FormField.vue'
  import RadioButtonGroupComponent from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import { QUESTION_OPTIONS } from '@/modules/core/constants/common'
  import { computed } from 'vue'
  import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
  import { isNumberKey } from '@/utils/input.utils'

  const { t, locale } = useI18n()
  const { required } = useRules()

  const i18nRoot = 'copy_questionnaire-form'

  interface PropsInterface {
    id: string
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})
  const question = defineModel<QuestionUsage>()

  const types = computed(() => getOptions(props.id, 'type'))
  // const amounts = computed(() => getOptions(props.id, 'amount'))

  const defaultType = computed(() => getDefaultOption(question.value!.type, props.id, 'type'))
  // const defaultAmount = computed(() => getDefaultOption(question.value!.amount, props.id, 'amount'))

  function getNumber(value: string): string {
    return (value.match(/\d{1,3}/g) ?? [''])[0]
  }

  function setAmount(value: string) {
    question.value!.amount = getNumber(value)
  }

  function getOptions(key: string, subKey: string) {
    try {
      if (subKey === 'amount') return []

      const allOptions = QUESTION_OPTIONS[locale.value as keyof typeof QUESTION_OPTIONS]
      const keyOptions = allOptions[key as keyof typeof allOptions]

      if (keyOptions && subKey in keyOptions) {
        const options = keyOptions[subKey as keyof typeof keyOptions]

        // * In Life questionnaire want to receive label as the value
        return options.map((o) => ({
          value: o.label,
          label: o.label
        }))
      }

      return []
    } catch (e) {
      console.error(e)
      return []
    }
  }

  function getDefaultOption(value: string, key: string, subKey: string) {
    const allOptions = getOptions(key, subKey)

    return value || allOptions[0].value
  }
</script>
